import { arrayUnion, doc, FieldValue, getDoc, setDoc } from 'firebase/firestore';
import { useCallback } from 'react';

import { database } from '@/config/firebase';
import logger from '@/logger';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const BACKOFF_MULTIPLIER = 2;
const INITIAL_RETRY_COUNT = 1;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

async function retryOperation<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY,
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (retries === 0) {
      throw error;
    }
    logger.warn(`Operation failed, retrying... (${retries} attempts left)`);
    await sleep(delay);
    return retryOperation(operation, retries - INITIAL_RETRY_COUNT, delay * BACKOFF_MULTIPLIER);
  }
}

type UpdateData = {
  [key: string]: FieldValue | string | number | boolean | null | undefined;
};

export default function useFirebase() {
  const appendToArrayField = useCallback(
    async (documentId: string, fieldName: string, value: unknown) => {
      return retryOperation(async () => {
        try {
          const documentReference = doc(database, 'student_cards_collection', documentId);
          const updateData: UpdateData = {
            [fieldName]: arrayUnion(value),
          };
          await setDoc(documentReference, updateData, { merge: true });
          logger.log(`Successfully added value to ${fieldName}:`, value);
        } catch (error) {
          logger.error(`Failed to add value to ${fieldName}:`, error);
          throw error;
        }
      });
    },
    [],
  );

  const getArrayField = useCallback(async function <T>(
    documentId: string,
    fieldName: string,
  ): Promise<T[]> {
    return retryOperation(async () => {
      try {
        const documentReference = doc(database, 'student_cards_collection', documentId);
        const documentSnap = await getDoc(documentReference);

        if (!documentSnap.exists()) {
          logger.warn(`Document ${documentId} does not exist`);
          return [];
        }

        const data = documentSnap.data();
        const fieldValue = (data[fieldName] || []) as T[];
        logger.log(`Successfully retrieved ${fieldName} array:`, fieldValue);
        return fieldValue;
      } catch (error) {
        logger.error(`Failed to get ${fieldName} array:`, error);
        throw error;
      }
    });
  }, []);

  return {
    appendToArrayField,
    getArrayField,
  };
}
